import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService, User, LoginCredentials, AuthResponse } from '@/services/auth';
import { clearPeDelegatedChildTenantId, getPeDelegatedChildTenantId } from '@/services/peDelegatedContext';
import { pePortfolioService } from '@/services/pePortfolioService';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<AuthResponse>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  updateToken: (token: string) => void;
  setUser: (user: User | null) => void;
  setTokenState: (token: string | null) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setTokenState] = useState<string | null>(authService.getToken());

  // Only consider authenticated when BOTH token and user data are fully loaded
  const isAuthenticated = !!user && !!token && !isLoading;

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check for existing token on app load
        const token = authService.getToken();
        
        if (token) {
          // Validate token by getting current user
          try {
            const userData = await authService.getCurrentUser();
            setUser(userData);
            setTokenState(token);
          } catch (error) {
            // Token is invalid, remove it
            authService.removeToken();
            setUser(null);
            setTokenState(null);
          }
        } else {
          setUser(null);
          setTokenState(null);
        }
      } catch (error) {
        console.error('AuthProvider: Error during initialization:', error);
        setUser(null);
        setTokenState(null);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      const authResponse = await authService.login(credentials);
      
      // Store token and verify it was stored
      authService.setToken(authResponse.token);
      
      // Update both user and token state
      setUser(authResponse.user);
      setTokenState(authResponse.token);
      
      // Return the auth response so components can access user data immediately
      return authResponse;
    } catch (error) {
      console.error('AuthProvider: Login failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      if (user?.tenantType === 'PORTFOLIO' && getPeDelegatedChildTenantId()) {
        try {
          await pePortfolioService.postDelegatedContext(null);
        } catch {
          /* non-blocking audit */
        }
      }
      await authService.logout();
    } catch (error) {
      // Even if logout API fails, clear local state
      console.error('Logout API failed:', error);
    } finally {
      clearPeDelegatedChildTenantId();
      setUser(null);
      setTokenState(null);
    }
  };

  const refreshToken = async () => {
    try {
      const { token } = await authService.refreshToken();
      authService.setToken(token);
    } catch (error) {
      // Token refresh failed, logout user
      await logout();
      throw error;
    }
  };

  const updateToken = (token: string) => {
    authService.setToken(token);
    setTokenState(token);
    // Note: User data should be updated separately by the calling component
    // to avoid race conditions with async getCurrentUser calls
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    refreshToken,
    updateToken,
    setUser,
    setTokenState,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 


